import loadMessageSuggestion from './onSuggestionSelectedHelpers/loadMessageSuggestion';
import showSxSForFileSuggestion from './onSuggestionSelectedHelpers/showSxSForFileSuggestion';
import isMailSearchAction from '../utils/isMailSearchAction';
import { is3SFeatureEnabled } from 'owa-mail-feature-flags';
import { SearchScenarioId } from 'owa-search-next';
import {
    lazyIsSearchBoxEmpty,
    onPillSuggestionSelected,
    onSearchTextChanged,
    onSuggestionSelected,
    startSearch,
    updateNextSearchQueryId,
    onGuidedFormulationSuggestionSelected,
} from 'owa-search-next-lazy';
import { orchestrator } from 'satcheljs';
import {
    Suggestion,
    KeywordSuggestion,
    PeopleSuggestion,
    MessageSuggestion,
    FileSuggestion,
    CategorySearchSuggestion,
    SuggestionKind,
    lazyGetLocalContentId,
    lazyLogEntityClicked,
    SubstrateSearchScenario,
} from 'owa-search-service';
import isInLazySearchExperiment from '../utils/isInLazySearchExperiment';

export default orchestrator(onSuggestionSelected, async actionMessage => {
    if (!isMailSearchAction(actionMessage.scenarioId)) {
        return;
    }

    const { suggestion, suggestionSetTraceId } = actionMessage;

    switch (suggestion.kind) {
        case SuggestionKind.Keywords: {
            handleKeywordSuggestion(suggestion, suggestionSetTraceId);
            break;
        }
        case SuggestionKind.People: {
            handlePeopleSuggestion(suggestion, suggestionSetTraceId);
            break;
        }
        case SuggestionKind.Category: {
            handleCategorySuggestion(suggestion, suggestionSetTraceId);
            break;
        }
        case SuggestionKind.Message: {
            handleMessageSuggestion(suggestion, suggestionSetTraceId);
            break;
        }
        case SuggestionKind.Files: {
            handleFileSuggestion(suggestion, suggestionSetTraceId);
            break;
        }
        default:
            break;
    }
});

// Log suggestion click if QF is enabled (and it's a QF suggestion).
async function logEntityClicked(
    suggestion: Suggestion,
    suggestionSetTraceId: string,
    localContentReferenceId?: string
) {
    /**
     * We shouldn't log entityclicked events when there's nothing in the search
     * box (i.e. zero query) because those suggestions aren't served by 3S.
     */
    const isSearchBoxEmpty = await lazyIsSearchBoxEmpty.import();
    if (
        isSearchBoxEmpty(SearchScenarioId.Mail) &&
        !(is3SFeatureEnabled('sea-zinq-3s3') || isInLazySearchExperiment())
    ) {
        return;
    }

    const referenceId = localContentReferenceId ? localContentReferenceId : suggestion.ReferenceId;

    if (is3SFeatureEnabled('sea-qf') && suggestionSetTraceId) {
        lazyLogEntityClicked.importAndExecute(
            suggestionSetTraceId,
            referenceId,
            new Date(),
            SubstrateSearchScenario.Mail
        );
    }
}

function handleKeywordSuggestion(suggestion: KeywordSuggestion, suggestionSetTraceId: string) {
    if (is3SFeatureEnabled('sea-gf') && suggestion.Segments && suggestion.Segments.length > 0) {
        // Update the search box (segments and text).
        onGuidedFormulationSuggestionSelected(
            suggestion.Segments,
            true /* suggestionSelected */,
            SearchScenarioId.Mail
        );
    } else {
        onSearchTextChanged(
            suggestion.QueryText,
            SearchScenarioId.Mail,
            false /* showSuggestionsUi */
        );
    }

    logEntityClicked(suggestion, suggestionSetTraceId);
    startSearch('SearchBoxSuggestionDropDown', SearchScenarioId.Mail, false /* explicitSearch */);
}

async function handlePeopleSuggestion(suggestion: PeopleSuggestion, suggestionSetTraceId: string) {
    onPillSuggestionSelected(suggestion, true /* suggestionSelected */, SearchScenarioId.Mail);

    if (suggestion.Source == 'qf' || suggestion.Source == 'gal') {
        let referenceId;
        if (suggestion.Source == 'gal') {
            const getLocalContentId = await lazyGetLocalContentId.import();
            referenceId = getLocalContentId(suggestion.ReferenceId);
        }

        logEntityClicked(
            suggestion,
            suggestionSetTraceId,
            referenceId /* localContentReferenceId */
        );
    }

    startSearch('SearchBoxSuggestionDropDown', SearchScenarioId.Mail, false /* explicitSearch */);
}

async function handleCategorySuggestion(
    suggestion: CategorySearchSuggestion,
    suggestionSetTraceId: string
) {
    onPillSuggestionSelected(suggestion, true /* suggestionSelected */, SearchScenarioId.Mail);

    const getLocalContentId = await lazyGetLocalContentId.import();
    const referenceId = getLocalContentId(suggestion.ReferenceId);
    logEntityClicked(suggestion, suggestionSetTraceId, referenceId /* localContentReferenceId */);

    startSearch('SearchBoxSuggestionDropDown', SearchScenarioId.Mail, false /* explicitSearch */);
}

function handleMessageSuggestion(suggestion: MessageSuggestion, suggestionSetTraceId: string) {
    logEntityClicked(suggestion, suggestionSetTraceId);
    loadMessageSuggestion(suggestion);

    // Bug 51227: Search conversation needs to be cleaned up after selecting direct nav QF suggestion
    updateNextSearchQueryId(SearchScenarioId.Mail);
}

async function handleFileSuggestion(suggestion: FileSuggestion, suggestionSetTraceId: string) {
    logEntityClicked(suggestion, suggestionSetTraceId);
    showSxSForFileSuggestion(suggestion);

    // Bug 51227: Search conversation needs to be cleaned up after selecting direct nav QF suggestion
    updateNextSearchQueryId(SearchScenarioId.Mail);
}
